/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react"
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Container,
  Avatar,
  CardActionArea,
  Tooltip,
  IconButton,
} from "@material-ui/core"
import {
  Delete as DeleteIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  FiberManualRecord as StatusIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Face,
  Category,
  ArrowBack,
  Error,
  EventNote as EventNoteIcon,
  SwapHoriz as SwapHorizIcon,
  LockOutlined,
} from "@material-ui/icons"
import { useSelector } from "react-redux"
import Page from "src/components/Page"
import axiosInstance from "src/utils/axios"
import { selectConjunto } from "src/redux/slices/complexSlice"
import { useSnackbar } from "notistack"
import BiometricLogsDialog from "./Dialogs/BiometricLogsDialog"
import MigrateRostrosDialog from "./Dialogs/MigrateRostrosDialog"
import useStyles from "./useStyles"

const ModuleLockedTooltip = ({ children }) => (
  <Tooltip
    title={
      <Box style={{ textAlign: "center", padding: "2px 0" }}>
        <Typography
          variant="caption"
          style={{ display: "block", fontWeight: 700 }}
        >
          Módulo no activado
        </Typography>
        <Typography
          variant="caption"
          style={{ display: "block", color: "#ffcc80" }}
        >
          Requiere activación
        </Typography>
      </Box>
    }
    arrow
    placement="top"
  >
    <span style={{ cursor: "not-allowed" }}>{children}</span>
  </Tooltip>
)

const BiometricDevicesIndex = ({ moduloBiometricosResidentes }) => {
  const classes = useStyles()
  const conjunto = useSelector(selectConjunto)
  const { enqueueSnackbar } = useSnackbar()
  const [devices, setDevices] = useState([])
  const [filteredDevices, setFilteredDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState("")
  const [isRefresh, setRefreshing] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deviceToDelete, setDeviceToDelete] = useState(null)
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [openControllerDialog, setOpenControllerDialog] = useState(false)
  const [openLogsDialog, setOpenLogsDialog] = useState(false)
  const [selectedLogDevice, setSelectedLogDevice] = useState(null)
  const [openMigrateDialog, setOpenMigrateDialog] = useState(false)
  const [migrateSourceDevice, setMigrateSourceDevice] = useState(null)

  const getBiometrics = async () => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      const { data } = await axiosInstance.get(
        `api/biometrics/conjuntos/${conjunto.id}/biometricos/status`
      )
      setDevices(data.data)
      const sortedDevices = sortDevicesByPriority(
        data?.data?.devices_status || []
      )
      setFilteredDevices(sortedDevices)
      setLoading(false)
      setRefreshing(false)
    } catch (error) {
      enqueueSnackbar(
        "Ocurrió un error inesperado, intentalo de nuevo más tarde",
        { variant: "error" }
      )
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleOpenLogs = (device, e) => {
    if (e) e.stopPropagation()
    setSelectedLogDevice({
      ip: device.ip,
      ipTail: device.ipTail || "",
      nombre: device.device_name || device.nombre,
      ubicacion: device.ubicacion,
      marca: device.marca,
      usuario: device.usuario || device.user || "admin",
      contraseña: device.contraseña || device.password || "",
    })
    setOpenLogsDialog(true)
  }

  const handleOpenMigrate = (device, e) => {
    if (e) e.stopPropagation()
    setMigrateSourceDevice({
      ...device,
      nombre: device.device_name || device.nombre,
      id: device.rostro_id || device.id || device.dispositivo_id,
    })
    setOpenMigrateDialog(true)
  }

  const getControllerStatus = () => {
    if (!devices || loading) return "loading"
    if (!devices.success) return "api_error"
    const controllerStatus = devices?.controller_status
    if (controllerStatus) return controllerStatus
    if (!devices?.devices_status || devices.devices_status.length === 0) {
      return "no_devices"
    }
    return "unknown"
  }

  const getControllerStatusColor = status => {
    switch (status?.toLowerCase()) {
      case "loading":
        return "#092147"
      default:
        return "#f8931f"
    }
  }

  const getControllerStatusText = status => {
    switch (status?.toLowerCase()) {
      case "loading":
        return "Verificando controladores..."
      default:
        return "Alertas controlador"
    }
  }

  const getControllerStatusIcon = status => {
    switch (status?.toLowerCase()) {
      case "loading":
        return <BuildIcon style={{ fontSize: 18, color: "white" }} />
      default:
        return <Error style={{ fontSize: 18, color: "white" }} />
    }
  }

  useEffect(() => {
    getBiometrics()
  }, [])

  const handleDeleteDevice = () => {
    if (deviceToDelete) {
      const updatedDevices = devices.filter(
        device => device.id !== deviceToDelete.id
      )
      setDevices(updatedDevices)
      setFilteredDevices(updatedDevices)
      enqueueSnackbar("Dispositivo eliminado exitosamente", {
        variant: "success",
      })
      setOpenDeleteDialog(false)
      setDeviceToDelete(null)
    }
  }

  const cancelSearch = () => {
    setSearched("")
    setFilteredDevices(devices)
  }

  const getStatusTranslation = status => {
    switch (status) {
      case "active":
        return { en: "Activo", connection: "En línea" }
      case "unknown":
        return { en: "Desconocido", connection: "Sin conexión" }
      default:
        return { en: "Inactivo", connection: "Fuera de línea" }
    }
  }

  const sortDevicesByPriority = dev => {
    if (!dev || !Array.isArray(dev)) return []
    return dev.sort((a, b) => {
      const aIsActive = a.status === "active"
      const bIsActive = b.status === "active"
      if (aIsActive && !bIsActive) return 1
      if (!aIsActive && bIsActive) return -1
      const dateA = new Date(a.timestamp || 0)
      const dateB = new Date(b.timestamp || 0)
      return dateB - dateA
    })
  }

  const formatDate = date => {
    if (!date) return "Sin fecha"
    try {
      return new Date(date).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch (error) {
      return "Fecha inválida"
    }
  }

  const getEstadoColor = estado => {
    switch (estado?.toLowerCase()) {
      case "active":
        return "#4CAF50"
      case "error":
      case "connection_error":
        return "#f44336"
      case "mantenimiento":
        return "#FF9800"
      default:
        return "#757575"
    }
  }

  const getEstadoIcon = status => {
    switch (status?.toLowerCase()) {
      case "active":
        return <CheckCircleIcon />
      case "error":
        return <ErrorIcon />
      case "mantenimiento":
        return <BuildIcon />
      default:
        return <StatusIcon />
    }
  }

  const handleBackToDevices = () => {
    setSelectedDevice(null)
  }

  return (
    <Page title="Dispositivos Biométricos" className={classes.root}>
      <Box className={classes.headerContainer}>
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "white",
          }}
        />
        <Container maxWidth="xl" style={{ position: "relative" }}>
          {selectedDevice && (
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToDevices}
              style={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "bold",
                borderColor: "#092147",
                color: "#092147",
                fontSize: "1rem",
              }}
            >
              {selectedDevice?.device_name}
            </Button>
          )}
          {devices?.ssh_failed_count > 0 && (
            <Box
              display="flex"
              alignItems="center"
              style={{ marginTop: "12px" }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={getControllerStatusIcon(getControllerStatus())}
                onClick={() => setOpenControllerDialog(true)}
                style={{
                  backgroundColor: getControllerStatusColor(
                    getControllerStatus()
                  ),
                  color: "white",
                  borderColor: getControllerStatusColor(getControllerStatus()),
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  height: "36px",
                  borderRadius: "18px",
                  textTransform: "none",
                }}
              >
                {getControllerStatusText(getControllerStatus())}
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {!selectedDevice && (
        <Container maxWidth="xl">
          {/* Estadísticas */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                className={`${classes.statsCard} ${classes.statsCardActive}`}
              >
                <CheckCircleIcon
                  style={{ fontSize: 30, marginBottom: "0.5rem" }}
                />
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  {devices?.devices_active || 0}
                </Typography>
                <Typography variant="body2">Activos</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                className={`${classes.statsCard} ${classes.statsCardMaintenance}`}
              >
                <BuildIcon style={{ fontSize: 30, marginBottom: "0.5rem" }} />
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  0
                </Typography>
                <Typography variant="body2">En Mantenimiento</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                className={`${classes.statsCard} ${classes.statsCardInactive}`}
              >
                <ErrorIcon style={{ fontSize: 30, marginBottom: "0.5rem" }} />
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  {devices?.devices_inactive || 0}
                </Typography>
                <Typography variant="body2">Inactivos</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                className={`${classes.statsCard} ${classes.statsCardTotal}`}
              >
                <SecurityIcon
                  style={{ fontSize: 30, marginBottom: "0.5rem" }}
                />
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                  {devices?.devices_configured || 0}
                </Typography>
                <Typography variant="body2">Total Dispositivos</Typography>
              </Paper>
            </Grid>
          </Grid>

          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              my={5}
            >
              <CircularProgress style={{ color: "#667eea" }} />
              <Typography variant="body2" style={{ marginLeft: "1rem" }}>
                Cargando dispositivos...
              </Typography>
            </Box>
          )}

          {!loading && (
            <Box>
              {filteredDevices.length > 0 ? (
                <Grid container spacing={3}>
                  {filteredDevices.map(device => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={device.rostro_id}
                    >
                      <Card
                        elevation={4}
                        className={classes.deviceCard}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform =
                            "translateY(-8px) scale(1.02)"
                          e.currentTarget.style.boxShadow =
                            "0 20px 40px rgba(0,0,0,0.15)"
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform =
                            "translateY(0) scale(1)"
                          e.currentTarget.style.boxShadow =
                            "0 8px 25px rgba(0,0,0,0.1)"
                        }}
                      >
                        <CardActionArea style={{ height: "100%" }}>
                          <Box
                            style={{
                              height: "140px",
                              background:
                                "linear-gradient(135deg, #092147 0%, #1e3a8a 100%)",
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Avatar
                              style={{
                                width: 80,
                                height: 80,
                                backgroundColor: "rgba(255,255,255,0.2)",
                                backdropFilter: "blur(10px)",
                                border: "2px solid rgba(255,255,255,0.3)",
                              }}
                            >
                              <Face style={{ fontSize: 40, color: "white" }} />
                            </Avatar>

                            <Box
                              style={{
                                position: "absolute",
                                top: "16px",
                                right: "16px",
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "rgba(255,255,255,0.2)",
                                backdropFilter: "blur(10px)",
                                borderRadius: "20px",
                                padding: "6px 12px",
                              }}
                            >
                              {React.cloneElement(
                                getEstadoIcon(device.status),
                                {
                                  style: {
                                    fontSize: 16,
                                    color: getEstadoColor(device.status),
                                    marginRight: "4px",
                                  },
                                }
                              )}
                              <Typography
                                variant="caption"
                                style={{ color: "white", fontWeight: "bold" }}
                              >
                                {getStatusTranslation(device.status).en}
                              </Typography>
                            </Box>

                            <Box
                              style={{
                                position: "absolute",
                                top: "16px",
                                left: "16px",
                                backgroundColor: getEstadoColor(device.status),
                                borderRadius: "12px",
                                padding: "4px 8px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <StatusIcon
                                style={{
                                  fontSize: 12,
                                  color: "white",
                                  marginRight: "4px",
                                  animation:
                                    device.status === "active"
                                      ? "pulse 2s infinite"
                                      : "none",
                                }}
                              />
                              <Typography
                                variant="caption"
                                style={{ color: "white", fontWeight: "bold" }}
                              >
                                {getStatusTranslation(device.status).connection}
                              </Typography>
                            </Box>

                            <Box
                              style={{
                                position: "absolute",
                                bottom: "12px",
                                right: "12px",
                                display: "flex",
                                gap: "6px",
                              }}
                            >
                              {moduloBiometricosResidentes ? (
                                <Tooltip title="Ver registros de acceso">
                                  <IconButton
                                    size="small"
                                    onClick={e => handleOpenLogs(device, e)}
                                    style={{
                                      backgroundColor: "rgba(255,255,255,0.18)",
                                      backdropFilter: "blur(8px)",
                                      border:
                                        "1px solid rgba(255,255,255,0.35)",
                                      color: "white",
                                      padding: "6px",
                                    }}
                                  >
                                    <EventNoteIcon style={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <ModuleLockedTooltip>
                                  <IconButton
                                    size="small"
                                    disabled
                                    style={{
                                      backgroundColor: "rgba(255,255,255,0.08)",
                                      border:
                                        "1px solid rgba(255,255,255,0.15)",
                                      color: "rgba(255,255,255,0.35)",
                                      padding: "6px",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    <LockOutlined style={{ fontSize: 18 }} />
                                  </IconButton>
                                </ModuleLockedTooltip>
                              )}

                              <Tooltip title="Migrar rostros a otro dispositivo">
                                <IconButton
                                  size="small"
                                  onClick={e => handleOpenMigrate(device, e)}
                                  style={{
                                    backgroundColor: "rgba(255,255,255,0.18)",
                                    backdropFilter: "blur(8px)",
                                    border: "1px solid rgba(255,255,255,0.35)",
                                    color: "white",
                                    padding: "6px",
                                  }}
                                >
                                  <SwapHorizIcon style={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <CardContent
                            style={{
                              padding: "24px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="h5"
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "8px",
                                  fontSize: "1.3rem",
                                  color: "#2c3e50",
                                }}
                              >
                                {device.device_name}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{
                                  marginBottom: "16px",
                                  fontFamily: "monospace",
                                  backgroundColor: "#f8fafc",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {device.ip}
                              </Typography>

                              <Box display="flex" alignItems="center" mb={2}>
                                <LocationIcon
                                  style={{
                                    fontSize: 18,
                                    color: "#667eea",
                                    marginRight: "8px",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: "500",
                                  }}
                                >
                                  {device.ubicacion}
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center" mb={2}>
                                <ScheduleIcon
                                  style={{
                                    fontSize: 18,
                                    color: "#667eea",
                                    marginRight: "8px",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  Fecha creación: {formatDate(device.timestamp)}
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center">
                                <Category
                                  style={{
                                    fontSize: 18,
                                    color: "#667eea",
                                    marginRight: "8px",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  Marca: {device.marca}
                                </Typography>
                              </Box>
                            </Box>

                            <Box
                              mt={2}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Chip
                                label={getStatusTranslation(device.status).en}
                                size="medium"
                                icon={React.cloneElement(
                                  getEstadoIcon(device.status),
                                  {
                                    style: { fontSize: 18, color: "white" },
                                  }
                                )}
                                style={{
                                  backgroundColor: getEstadoColor(
                                    device.status
                                  ),
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.8rem",
                                  height: "32px",
                                  borderRadius: "16px",
                                }}
                              />

                              <Box display="flex" style={{ gap: "6px" }}>
                                {/* Logs — bloqueado si módulo inactivo */}
                                {moduloBiometricosResidentes ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <EventNoteIcon style={{ fontSize: 14 }} />
                                    }
                                    onClick={e => handleOpenLogs(device, e)}
                                    style={{
                                      textTransform: "none",
                                      fontWeight: 700,
                                      fontSize: "0.72rem",
                                      borderColor: "#092147",
                                      color: "#092147",
                                      borderRadius: 20,
                                      padding: "3px 10px",
                                    }}
                                  >
                                    Logs
                                  </Button>
                                ) : (
                                  <ModuleLockedTooltip>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled
                                      startIcon={
                                        <LockOutlined
                                          style={{ fontSize: 14 }}
                                        />
                                      }
                                      style={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "0.72rem",
                                        borderRadius: 20,
                                        padding: "3px 10px",
                                        pointerEvents: "none",
                                        opacity: 0.45,
                                      }}
                                    >
                                      Logs
                                    </Button>
                                  </ModuleLockedTooltip>
                                )}

                                {/* Migrar */}
                                <Tooltip title="Migrar todos los rostros a otro dispositivo">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <SwapHorizIcon style={{ fontSize: 14 }} />
                                    }
                                    onClick={e => handleOpenMigrate(device, e)}
                                    style={{
                                      textTransform: "none",
                                      fontWeight: 700,
                                      fontSize: "0.72rem",
                                      borderColor: "#1e3a8a",
                                      color: "#1e3a8a",
                                      borderRadius: 20,
                                      padding: "3px 10px",
                                      backgroundColor: "#f0f4ff",
                                    }}
                                  >
                                    Migrar
                                  </Button>
                                </Tooltip>
                              </Box>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper elevation={3} className={classes.emptyStateContainer}>
                  {searched ? (
                    <Box>
                      <SearchIcon
                        style={{
                          fontSize: 80,
                          color: "#cbd5e0",
                          marginBottom: "2rem",
                        }}
                      />
                      <Typography
                        variant="h5"
                        color="textSecondary"
                        style={{ marginBottom: "1rem", fontWeight: "bold" }}
                      >
                        No se encontraron dispositivos
                      </Typography>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        style={{ marginBottom: "2rem" }}
                      >
                        No hay dispositivos que coincidan con:{" "}
                        <strong>{searched}</strong>
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={cancelSearch}
                        style={{
                          backgroundColor: "#667eea",
                          color: "white",
                          borderRadius: "12px",
                          padding: "12px 30px",
                          textTransform: "none",
                          fontSize: "1rem",
                        }}
                      >
                        Limpiar búsqueda
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <SecurityIcon
                        style={{
                          fontSize: 80,
                          color: "#cbd5e0",
                          marginBottom: "2rem",
                        }}
                      />
                      <Typography
                        variant="h5"
                        color="textSecondary"
                        style={{ marginBottom: "1rem", fontWeight: "bold" }}
                      >
                        No hay dispositivos configurados
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Los dispositivos biométricos aparecerán aquí una vez
                        configurados
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          )}

          {/* Dialog eliminación */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: "16px" } }}
          >
            <DialogTitle
              style={{
                backgroundColor: "#ffebee",
                borderRadius: "16px 16px 0 0",
              }}
            >
              <Box display="flex" alignItems="center">
                <WarningIcon
                  style={{ color: "#f44336", marginRight: "0.5rem" }}
                />
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Confirmar Eliminación
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent style={{ padding: "2rem" }}>
              <Typography variant="body1" style={{ marginBottom: "1rem" }}>
                ¿Está seguro que desea eliminar el dispositivo:{" "}
                <strong>{deviceToDelete?.nombre}</strong>?
              </Typography>
              <Box
                style={{
                  backgroundColor: "#fff3e0",
                  padding: "1rem",
                  borderRadius: "12px",
                  border: "1px solid #ffcc02",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  <strong>Advertencia:</strong> Esta acción no se puede
                  deshacer.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions style={{ padding: "1rem 2rem" }}>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                color="primary"
                variant="outlined"
                style={{ borderRadius: "8px" }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteDevice}
                color="secondary"
                variant="contained"
                startIcon={<DeleteIcon />}
                style={{ backgroundColor: "#f44336", borderRadius: "8px" }}
              >
                Eliminar
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      )}

      {/* Dialog controladores */}
      <Dialog
        open={openControllerDialog}
        onClose={() => setOpenControllerDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ style: { borderRadius: "16px" } }}
      >
        <DialogTitle
          style={{ backgroundColor: "#f5f5f5", borderRadius: "16px 16px 0 0" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <SecurityIcon
                style={{ color: "#092147", marginRight: "0.5rem" }}
              />
              <Typography
                variant="h6"
                style={{ fontWeight: "bold" }}
                color="primary"
              >
                Estado de Controladores ({devices?.raspberrys_total || 0}{" "}
                Controladores)
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={`${devices?.ssh_success_count || 0} Conectados`}
                size="small"
                style={{ backgroundColor: "#4CAF50", color: "white" }}
              />
              <Chip
                label={`${devices?.ssh_failed_count || 0} Desconectados`}
                size="small"
                style={{ backgroundColor: "#f44336", color: "white" }}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: "2rem" }}>
          {devices?.raspberrys && devices.raspberrys.length > 0 ? (
            <Grid container spacing={3}>
              {devices.raspberrys.map((raspberry, index) => {
                const hasSSHConnection = devices.devices_status.some(
                  device =>
                    device.raspberry_id === raspberry.raspberry_id &&
                    device.ssh_status === "connected"
                )
                const raspberryDevices = devices.devices_status.filter(
                  device => device.raspberry_id === raspberry.raspberry_id
                )

                return (
                  <Grid item xs={12} sm={6} md={4} key={raspberry.raspberry_id}>
                    <Card
                      elevation={3}
                      style={{
                        borderRadius: "16px",
                        border: `3px solid ${hasSSHConnection ? "#4CAF50" : "#f44336"}`,
                        background: hasSSHConnection
                          ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                          : "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                      }}
                    >
                      <CardContent style={{ padding: "20px" }}>
                        <Box display="flex" alignItems="center" mb={2}>
                          {hasSSHConnection ? (
                            <CheckCircleIcon
                              style={{
                                color: "#4CAF50",
                                marginRight: "8px",
                                fontSize: 24,
                              }}
                            />
                          ) : (
                            <ErrorIcon
                              style={{
                                color: "#f44336",
                                marginRight: "8px",
                                fontSize: 24,
                              }}
                            />
                          )}
                          <Box>
                            <Typography
                              variant="h6"
                              style={{ fontWeight: "bold", lineHeight: 1 }}
                            >
                              Controlador #{index + 1}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {raspberry.raspberry_id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          mb={2}
                          p={1.5}
                          style={{
                            backgroundColor: "rgba(255,255,255,0.7)",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography variant="body2" gutterBottom>
                            <strong>🌐 IP:</strong> {raspberry.ssh_config.ip}:
                            {raspberry.ssh_config.puerto}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>👤 Usuario:</strong>{" "}
                            {raspberry.ssh_config.usuario}
                          </Typography>
                          <Typography variant="body2">
                            <strong>🔧 Dispositivos:</strong>{" "}
                            {raspberryDevices.length}
                          </Typography>
                        </Box>

                        {raspberryDevices.length > 0 && (
                          <Box mb={2}>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: "bold" }}
                              gutterBottom
                            >
                              Dispositivos en este controlador:
                            </Typography>
                            <Box mt={1}>
                              {raspberryDevices.slice(0, 2).map(device => (
                                <Box
                                  key={device.rostro_id}
                                  display="flex"
                                  alignItems="center"
                                  mb={0.5}
                                >
                                  <Box
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor:
                                        device.status === "active"
                                          ? "#4CAF50"
                                          : device.status === "unknown"
                                            ? "#FF9800"
                                            : "#f44336",
                                      marginRight: 8,
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    {device.device_name || device.ip}
                                  </Typography>
                                </Box>
                              ))}
                              {raspberryDevices.length > 2 && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  +{raspberryDevices.length - 2} más...
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Chip
                            label={
                              hasSSHConnection ? "Conectado" : "Desconectado"
                            }
                            size="small"
                            style={{
                              backgroundColor: hasSSHConnection
                                ? "#4CAF50"
                                : "#f44336",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          ) : (
            <Box textAlign="center" py={6}>
              <SecurityIcon
                style={{ fontSize: 80, color: "#ccc", marginBottom: "2rem" }}
              />
              <Typography variant="h5" color="textSecondary" gutterBottom>
                No hay controladores configurados
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Configure al menos un controlador Raspberry Pi para gestionar
                dispositivos biométricos
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "1rem 2rem" }}>
          <Button
            onClick={() => setOpenControllerDialog(false)}
            color="primary"
            variant="contained"
            style={{ borderRadius: "8px", backgroundColor: "#092147" }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <BiometricLogsDialog
        open={openLogsDialog}
        onClose={() => {
          setOpenLogsDialog(false)
          setSelectedLogDevice(null)
        }}
        device={selectedLogDevice}
      />

      <MigrateRostrosDialog
        open={openMigrateDialog}
        onClose={() => {
          setOpenMigrateDialog(false)
          setMigrateSourceDevice(null)
        }}
        sourceDevice={migrateSourceDevice}
        allDevices={filteredDevices}
        conjuntoId={conjunto?.id}
      />
    </Page>
  )
}

export default BiometricDevicesIndex
